import { AskTextUpdate } from "./askStream";

/**
 * Ask's answer, shown word by word (claude/ask-stream-text-2, 2026-10-22).
 *
 * The Claude API holds back the start of a reply that follows lookup results
 * (measured on staging 2026-10-22: nothing for the whole turn, then up to
 * ~2,000 characters at once; longer replies stream normally after that). Ask's
 * answers come after lookups and are shorter than that, so their `text` events
 * arrive in one burst, right before the final answer. This pacer shows such a
 * burst a few words at a time instead of all at once, and catches up with text
 * that really streams. It never delays the first words: they are shown on the
 * next tick (30 ms).
 */

/** Pause between two reveal steps. */
export const REVEAL_TICK_MS = 30;
/** A burst is shown within this many ticks (~750 ms), at least a word per tick. */
export const REVEAL_TICKS = 25;
/** Once the final answer is in, the rest is shown within this many ticks (~240 ms). */
export const FINISH_TICKS = 8;

/**
 * How many characters to show next: the backlog spread over `ticks` steps, at
 * least one character, always ending at the end of a word (or of the text).
 */
export const nextShownLength = (
  text: string,
  shown: number,
  ticks: number,
): number => {
  if (shown >= text.length) return text.length;
  const remaining = text.length - shown;
  const step = Math.max(1, Math.ceil(remaining / Math.max(1, ticks)));
  let end = Math.min(text.length, shown + step);
  while (end < text.length && !/\s/.test(text[end])) end += 1;
  return end;
};

type Schedule = (run: () => void, ms: number) => unknown;
type Cancel = (handle: unknown) => void;

export class TextReveal {
  private target = "";

  private shown = 0;

  private ticks = REVEAL_TICKS;

  private finishing = false;

  private handle: unknown = null;

  private caughtUp: (() => void) | null = null;

  constructor(
    private readonly show: (text: string) => void,
    private readonly schedule: Schedule = (run, ms) => setTimeout(run, ms),
    private readonly cancel: Cancel = (handle) =>
      clearTimeout(handle as ReturnType<typeof setTimeout>),
  ) {}

  /** A `text` event: reset = forget what was shown (that turn did lookups). */
  push(update: AskTextUpdate) {
    if (update.reset) {
      this.target = "";
      this.shown = 0;
      this.show("");
    }
    if (!update.delta) return;
    this.target += update.delta;
    if (!this.finishing) this.ticks = REVEAL_TICKS;
    this.start();
  }

  /** The final answer arrived: show the rest quickly, then call `then`. */
  finish(then: () => void) {
    this.finishing = true;
    this.ticks = Math.min(this.ticks, FINISH_TICKS);
    if (this.shown >= this.target.length) {
      this.stop();
      then();
      return;
    }
    this.caughtUp = then;
    this.start();
  }

  /** Stopped / closed / failed: no more updates, `finish` never calls back. */
  stop() {
    if (this.handle !== null) this.cancel(this.handle);
    this.handle = null;
    this.caughtUp = null;
  }

  private start() {
    if (this.handle === null) {
      this.handle = this.schedule(this.tick, REVEAL_TICK_MS);
    }
  }

  private tick = () => {
    this.handle = null;
    this.shown = nextShownLength(this.target, this.shown, this.ticks);
    this.ticks = Math.max(1, this.ticks - 1);
    this.show(this.target.slice(0, this.shown));
    if (this.shown < this.target.length) {
      this.start();
      return;
    }
    const then = this.caughtUp;
    this.caughtUp = null;
    then?.();
  };
}
